import { Component, ElementRef, OnInit, QueryList, ViewChildren } from "@angular/core";
import { Router } from "@angular/router";
import { OrderService } from "src/app/services/order/order.service";
import { OrderPageResponse, OrderStatus, OrderAttributes, NonReviewableOrderStatuses } from "@models/order.model";
import { environment } from "src/environments/environment";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PaymentMethod, PaymentStatus, RefundStatus } from "@models/payment.model";
import { finalize } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { OrderItemAttributes } from "@models/orderItem.model";
import { ProductService } from "../../services/product/product.service";
import { DialogService } from "../../services/dialog/dialog.service";
import { PageEvent } from "@angular/material/paginator";

@Component({
    selector: "app-track-order",
    templateUrl: "./track-order.component.html",
    styleUrls: ["./track-order.component.scss"],
    standalone: false,
})
export class TrackOrderComponent implements OnInit {
    public orders: OrderAttributes[] = [];
    public totalOrders = 0;
    public visibleOrders: OrderAttributes[] = [];
    public paymentMethod = PaymentMethod;
    public paymentStatus = PaymentStatus;
    public orderStatus = OrderStatus;
    public refundStatus = RefundStatus;
    public returnReason!: string;
    public exchangeReason!: string;
    public showReturnPopup = false;
    public currentOrder: OrderAttributes | undefined;
    public currentPageIndex = environment.dynamicPagination.pageIndex;
    public pageSize = environment.dynamicPagination.pageSize;
    public showHelpDialog = false;
    public helpQuery = "";
    public isOkButtonEnabled = false;
    public returnForm: FormGroup;
    public loading: boolean = true;
    public recentDeliveredOrExchangedItemIds!: number[];
    public exceedMoreThan7daysIds!: number[];


    @ViewChildren("orderCard") orderCards!: QueryList<ElementRef>;
    private lastScrollIndex: number | null = null;
    public NonReviewableOrderStatuses = NonReviewableOrderStatuses;


    constructor(
        private orderService: OrderService,
        private router: Router,
        private formBuilder: FormBuilder,
        private translate: TranslateService,
    ) {
        this.returnForm = this.formBuilder.group({
            feedback: ["", Validators.required],
        });
    }

    ngOnInit(): void {
        this.loadOrders();
        this.getRecentDeliveredOrExchangedItemIds();
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    ngAfterViewInit(): void {
        this.orderCards.changes.subscribe(() => {
            if (this.lastScrollIndex !== null) {
                const cardToScroll = this.orderCards.toArray()[this.lastScrollIndex];
                if (cardToScroll) {
                    cardToScroll.nativeElement.scrollIntoView({ behavior: "auto", block: "start" });
                }
                this.lastScrollIndex = null;
            }
        });
    }

    public loadOrders(): void {
        this.loading = true;
        this.orderService
            .getOrder(this.currentPageIndex)
            .pipe(
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (response: OrderPageResponse) => {
                    if (response?.rows?.length > 0) {
                        const previousLength = this.orders.length;
                        this.totalOrders = response.count;
                        if (this.currentPageIndex === 0) {
                            this.orders = response.rows;
                        } else {
                            this.orders = response.rows;
                        }
                        
                        this.updateVisibleOrders();
                        this.lastScrollIndex = previousLength;
                    }
                },
                error: (error) => {
                    throw error;
                },
            });
    }

    public isOrderFullyCancelledOrReturned(order: OrderAttributes): boolean {
        return order.orderItems!.every(
            (item) => item.itemStatus === OrderStatus.Cancelled || item.itemStatus === OrderStatus.Returned,
        );
    }

    public refundStatusMessage(order: OrderAttributes): string {
        return order.orderItems!.every(
            (item) => item.refund !== null && item.refund?.refundStatus === RefundStatus.RefundSuccessful,
        )
            ? RefundStatus.RefundSuccessful
            : "";
    }

    public hasCancelledItems(order: OrderAttributes): boolean {
        return order.orderItems?.some((item) => item.itemStatus === OrderStatus.Cancelled)!;
    }

    /*** Next page ***/
    public goToNextPage(): void {
        this.currentPageIndex++;
        this.loadOrders();
    }

    /*** Next page ***/
    public goToPreviousPage(): void {
        this.currentPageIndex--;
        this.loadOrders();
    }

    public get itemPerPage(): string {
        const start = this.currentPageIndex * this.pageSize + 1;
        const end = Math.min(
            (this.currentPageIndex + 1) * this.pageSize,
            this.totalOrders
        );

        return `${start} - ${end}`;
    }

    public isItemOrderedOrPackedExist(order: OrderAttributes): boolean {
        return order.orderItems?.some(
            (item) => item.itemStatus === OrderStatus.Ordered || item.itemStatus === OrderStatus.Packed,
        )!;
    }

    public isAbleToReview(order: OrderAttributes): boolean {
        return order.orderItems?.some((item) => NonReviewableOrderStatuses.includes(item.itemStatus))!;
    }

    public isCancellableOrReturnOrder(order: OrderAttributes): string {
        const hasCancelledItem = order.orderItems?.some((item) => item.itemStatus === OrderStatus.Cancelled);

        const hasReturnedItems = order.orderItems?.some(
            (item) =>
                item.itemStatus === OrderStatus.ReturnInitiated ||
                item.itemStatus === OrderStatus.PickupScheduled ||
                item.itemStatus === OrderStatus.Returned,
        );

        const hasExchangeItems = order.orderItems?.some(
            (item) =>
                item.itemStatusHistory?.some(
                    (itemStatusHistory) =>
                        itemStatusHistory.status === OrderStatus.ExchangeInitiated ||
                        itemStatusHistory.status === OrderStatus.ExchangePacked ||
                        itemStatusHistory.status === OrderStatus.ExchangeDispatched ||
                        itemStatusHistory.status === OrderStatus.Exchanged,
                ),
        );

        const isDeliveredOrExchanged =
            order.orderStatus === OrderStatus.Delivered || order.orderStatus === OrderStatus.Exchanged;

        // Show "Return" only if the order is Delivered/Exchanged and no return is initiated
        if (
            isDeliveredOrExchanged &&
            !hasReturnedItems &&
            this.isAllItemsRecentlyDeliveredOrExchanged(order.orderItems!)
        ) {
            return "Return";
        }

        // Show "Cancel" if the order can still be cancelled and no exchange or return exists
        if (
            [OrderStatus.Ordered, OrderStatus.Packed].includes(order.orderStatus as OrderStatus) &&
            !hasCancelledItem &&
            !hasExchangeItems &&
            !hasReturnedItems
        ) {
            return "Cancel";
        }

        return "";
    }

    public hasReturnedItems(order: OrderAttributes): boolean {
        return order.orderItems?.some(
            (item) =>
                item.itemStatus === OrderStatus.ReturnInitiated ||
                item.itemStatus === OrderStatus.PickupScheduled ||
                item.itemStatus === OrderStatus.Returned,
        )!;
    }

    public hasExchangeItems(order: OrderAttributes): boolean {
        return order.orderItems?.some(
            (item) =>
                item.itemStatusHistory?.some(
                    (itemStatusHistory) =>
                        itemStatusHistory.status === OrderStatus.ExchangeInitiated ||
                        itemStatusHistory.status === OrderStatus.ExchangePacked ||
                        itemStatusHistory.status === OrderStatus.ExchangeDispatched ||
                        itemStatusHistory.status === OrderStatus.Exchanged,
                ),
        )!;
    }

    public showViewMoreButton(): boolean {
        return (this.currentPageIndex + 1) * environment.dynamicPagination.pageSize < this.totalOrders;
    }

    public updateVisibleOrders(): void {
        let endIndex = (this.currentPageIndex + 1) * environment.dynamicPagination.pageSize;
        endIndex = Math.min(endIndex, this.orders.length);
        this.visibleOrders = this.orders.slice(0, endIndex);
    }

    public viewOrderDetails(order: OrderAttributes): void {
        this.router.navigate(["/orderDetails", order.id]);
    }

    public getTotalItemsLeft(order: OrderAttributes): number {
        return order.orderItems?.length! - 1;
    }

    public goBackToProducts(): void {
        this.router.navigate(["/home"]);
    }

    public getRecentDeliveredOrExchangedItemIds(): void {
        this.orderService.getRecentDeliveredOrExchangedItemIds().subscribe({
            next: (response) => {
                this.recentDeliveredOrExchangedItemIds = response;
            },
        });
    }

    public isRecentDeliveredOrExchangedItem(item: OrderItemAttributes): boolean {
        return this.recentDeliveredOrExchangedItemIds?.includes(item.id!);
    }

    public isAllItemsRecentlyDeliveredOrExchanged(items: OrderItemAttributes[]): boolean {
        return items.every((item) => this.isRecentDeliveredOrExchangedItem(item));
    }

    private isRecentlyDeliveredOrExchangedItemExists(items: OrderItemAttributes[]): boolean {
        return items.some((item) => this.isRecentDeliveredOrExchangedItem(item));
    }

    public isReturnable(order: OrderAttributes): boolean {
        return !this.hasReturnedItems(order) && this.isRecentlyDeliveredOrExchangedItemExists(order.orderItems!);
    }

    public isExchangeable(order: OrderAttributes): boolean {
        return !this.hasExchangeItems(order) && this.isRecentlyDeliveredOrExchangedItemExists(order.orderItems!);
    }

    public getAddressTooltip(order: OrderAttributes): string {
        return `${order.customerName}
        ${order.orderAddress?.shippingAddress.address}
        ${order.orderAddress?.shippingAddress.city}, ${order.orderAddress?.shippingAddress.state} - ${order.orderAddress?.shippingAddress.pincode}`;
    }

    public navigateReviewPage(orderId: number): void {
        this.router.navigate([`/account/orders/${orderId}/itemsReview`]);
    }

    public navigateItemList(order: OrderAttributes, type: string): void {       
        if ((type === "return" && !this.isReturnable(order)) || (type === "exchange" && !this.isExchangeable(order))) {
            return;
        }

        this.router.navigate([`/account/orders/${order.id}/${type}`]);
    }

    public navigateOrderPage(orderId: number): void {
        this.router.navigate([`/account/orders/${orderId}/orderInfo`]);
    }

    public goBack(): void {
        this.router.navigate(["/account"]);
    }

    /****** To navigate productDetail Page  ***/
    public onClickProduct(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public handlePageEvent(event: PageEvent) {
        this.currentPageIndex = event.pageIndex;
        this.loadOrders();
    }

    public goToWishlist() {
        this.router.navigate(["/wishlist"]);
    }

    public goToBestSeller() {
        this.router.navigate(["/products"], { queryParams: { category: "bestSeller" } });
    }

    public goToFeature() {
        this.router.navigate(["/products"], { queryParams: { category: "featured" } });
    }
}
